import { ReactNode } from 'react';
import { SM_VIEWPORT } from '@constants/viewport';

export type ModalCloseSource = 'backdrop' | 'drag';

/** Returns true when the close was triggered by an implicit gesture (backdrop/drag) on a mobile viewport. */
export const isMobileImplicitDismiss = (source?: ModalCloseSource): boolean =>
  (source === 'backdrop' || source === 'drag') && window.innerWidth < SM_VIEWPORT;

export interface ModalV2Props {
  // Core (required)
  isOpen: boolean;
  onClose: (source?: ModalCloseSource) => void;
  children: ReactNode;

  // Variants
  variant?: 'sm' | 'md' | 'lg' | 'xl' | 'full';

  // Mobile features
  hasMobileHandle?: boolean;
  enableDragToClose?: boolean;

  // Styling
  maxWidthClass?: string;
  borderRadiusClass?: string;
  overlayClassName?: string;
  contentClassName?: string;
  /**
   * Constrains the modal (backdrop + centering box) to a sub-surface of the viewport instead of
   * `inset-0` — e.g. a side drawer's geometry (`inset-y-0 right-0 w-…`), so the modal centers
   * within that surface. Replaces the default `inset-0` / `w-screen` on both fixed layers.
   */
  surfaceClassName?: string;
  /**
   * Renders a floating centered dialog (with gutters) on mobile too, instead of the default
   * bottom-sheet placement. Pair with `hasMobileHandle={false}`.
   */
  centerOnMobile?: boolean;

  // Behavior
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;

  // Advanced
  enableMultipleModals?: boolean;
  zIndex?: number;

  // Lifecycle
  onAfterLeave?: () => void;

  // Accessibility
  ariaLabel?: string;
}

// For the drag-to-close hook
export interface DragToCloseOptions {
  isOpen: boolean;
  onClose: (source?: ModalCloseSource) => void;
  enabled?: boolean;
  threshold?: number;
  closingAnimationDuration?: number;
}

export interface UseDragToCloseReturn {
  panelRef: React.RefObject<HTMLDivElement>;
  isClosing: boolean;
  handleTouchStart: (e: React.TouchEvent) => void;
  handleTouchMove: (e: React.TouchEvent) => void;
  handleTouchEnd: () => void;
  handleTouchCancel: () => void;
  initiateClose: (source?: ModalCloseSource) => void;
}
